import { supabase } from '@/lib/supabase';
import { PageAnalytics } from './types';

export async function getProjectAnalytics(projectId: string): Promise<PageAnalytics | null> {
  try {
    // Get project stats
    const { data: project } = await supabase
      .from('projects')
      .select('views, conversions')
      .eq('id', projectId)
      .single();

    if (!project) return null;

    // Get time-based stats from analytics_events
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    // Get views today
    const { count: viewsToday } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'page_view')
      .gte('created_at', todayStart);

    // Get views this week
    const { count: viewsThisWeek } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'page_view')
      .gte('created_at', weekStart);

    // Get views this month
    const { count: viewsThisMonth } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'page_view')
      .gte('created_at', monthStart);

    // Get unique views
    const { data: uniqueViews } = await supabase
      .from('analytics_events')
      .select('visitor_id')
      .eq('project_id', projectId)
      .eq('event_type', 'page_view');

    const uniqueViewCount = new Set(uniqueViews?.map(v => v.visitor_id) || []).size;

    // Get signups today
    const { count: signupsToday } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'signup')
      .gte('created_at', todayStart);

    // Get signups this week
    const { count: signupsThisWeek } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'signup')
      .gte('created_at', weekStart);

    // Get signups this month
    const { count: signupsThisMonth } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'signup')
      .gte('created_at', monthStart);

    // Calculate average time on page
    const { data: timeData } = await supabase
      .from('analytics_events')
      .select('metadata')
      .eq('project_id', projectId)
      .eq('event_type', 'scroll_depth');

    const times = timeData?.map(t => t.metadata?.time_on_page || 0).filter(t => t > 0) || [];
    const avgTime = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;

    const views = project.views || 0;
    const signups = project.conversions || 0;

    return {
      project_id: projectId,
      views,
      unique_views: uniqueViewCount,
      signups,
      conversion_rate: views > 0 ? (signups / views) * 100 : 0,
      avg_time_on_page: Math.round(avgTime),
      views_today: viewsToday || 0,
      views_this_week: viewsThisWeek || 0,
      views_this_month: viewsThisMonth || 0,
      signups_today: signupsToday || 0,
      signups_this_week: signupsThisWeek || 0,
      signups_this_month: signupsThisMonth || 0,
    };
  } catch (error) {
    console.error('Failed to get project analytics:', error);
    return null;
  }
}

export async function getAnalyticsChartData(
  projectId: string,
  days?: number,
  startDate?: Date,
  endDate?: Date
): Promise<{ date: string; views: number; signups: number; conversionRate: number }[]> {
  try {
    let queryStart: Date;
    let queryEnd: Date;
    let totalDays: number;

    if (startDate && endDate) {
      queryStart = new Date(startDate);
      queryStart.setHours(0, 0, 0, 0);
      queryEnd = new Date(endDate);
      queryEnd.setHours(23, 59, 59, 999);
      totalDays = Math.ceil((queryEnd.getTime() - queryStart.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      totalDays = days || 30;
      queryStart = new Date();
      queryStart.setDate(queryStart.getDate() - totalDays);
      queryStart.setHours(0, 0, 0, 0);
      queryEnd = new Date();
      queryEnd.setHours(23, 59, 59, 999);
    }

    const { data: events } = await supabase
      .from('analytics_events')
      .select('event_type, created_at')
      .eq('project_id', projectId)
      .in('event_type', ['page_view', 'signup'])
      .gte('created_at', queryStart.toISOString())
      .lte('created_at', queryEnd.toISOString())
      .order('created_at', { ascending: true });

    // Group by date
    const dateMap: Record<string, { views: number; signups: number }> = {};

    // Initialize all dates in range
    for (let i = 0; i <= totalDays; i++) {
      const date = new Date(queryStart);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      dateMap[dateStr] = { views: 0, signups: 0 };
    }

    // Count events
    events?.forEach(event => {
      const dateStr = event.created_at.split('T')[0];
      if (dateMap[dateStr]) {
        if (event.event_type === 'page_view') {
          dateMap[dateStr].views++;
        } else if (event.event_type === 'signup') {
          dateMap[dateStr].signups++;
        }
      }
    });

    return Object.entries(dateMap).map(([date, data]) => ({
      date,
      views: data.views,
      signups: data.signups,
      conversionRate: data.views > 0 ? Math.round((data.signups / data.views) * 1000) / 10 : 0,
    }));
  } catch (error) {
    console.error('Failed to get analytics chart data:', error);
    return [];
  }
}

export async function getProjectStatsByDateRange(projectId: string, startDate: Date, endDate: Date) {
  try {
    // Ensure date boundaries are correct (Start of StartDate to End of EndDate)
    const queryStartDate = new Date(startDate);
    queryStartDate.setHours(0, 0, 0, 0);
    
    const queryEndDate = new Date(endDate);
    queryEndDate.setHours(23, 59, 59, 999);

    // Current Period Stats
    const { count: views } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'page_view')
      .gte('created_at', queryStartDate.toISOString())
      .lte('created_at', queryEndDate.toISOString());

    const { count: signups } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'signup')
      .gte('created_at', queryStartDate.toISOString())
      .lte('created_at', queryEndDate.toISOString());

    // Previous Period Stats (for Growth)
    // Calculate duration in exact days to shift back safely
    const durationMs = queryEndDate.getTime() - queryStartDate.getTime();
    const prevEndDate = new Date(queryStartDate.getTime() - 1); // 1ms before start
    const prevStartDate = new Date(prevEndDate.getTime() - durationMs);
    
    const { count: prevViews } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'page_view')
      .gte('created_at', prevStartDate.toISOString())
      .lte('created_at', prevEndDate.toISOString());

    const { count: prevSignups } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('event_type', 'signup')
      .gte('created_at', prevStartDate.toISOString())
      .lte('created_at', prevEndDate.toISOString());

    // Calculate Growth
    const calculateGrowth = (current: number, previous: number): number | null => {
      if (previous === 0) return null; // Cannot calculate growth from 0
      return Math.round(((current - previous) / previous) * 100);
    };

    const viewsCount = views || 0;
    const signupsCount = signups || 0;
    const prevViewsCount = prevViews || 0;
    const prevSignupsCount = prevSignups || 0;

    const conversionRate = viewsCount > 0 ? (signupsCount / viewsCount) * 100 : 0;
    const prevConversionRate = prevViewsCount > 0 ? (prevSignupsCount / prevViewsCount) * 100 : 0;

    return {
      views: viewsCount,
      viewsGrowth: calculateGrowth(viewsCount, prevViewsCount),
      signups: signupsCount,
      signupsGrowth: calculateGrowth(signupsCount, prevSignupsCount),
      conversionRate,
      conversionRateGrowth: calculateGrowth(conversionRate, prevConversionRate)
    };
  } catch (error) {
    console.error('Failed to get stats by date range:', error);
    return {
      views: 0,
      viewsGrowth: 0,
      signups: 0,
      signupsGrowth: 0,
      conversionRate: 0,
      conversionRateGrowth: 0
    };
  }
}
